import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useState } from "react"
import { useAddDoctorMedia } from "@/services/query/doctors.query"
import { toast } from "sonner"
import { FileUp } from "lucide-react"

const initialState = {
  title: "",
  description: "",
  file_link: "",
  source: "",
  media_file: null,
}

export function AddMediaDialog({ open, onOpenChange, doctorId }) {
  const [formData, setFormData] = useState(initialState)
  const [filePreview, setFilePreview] = useState(null)
  const addMediaMutation = useAddDoctorMedia(doctorId)

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFormData((prev) => ({ ...prev, media_file: file }))
      setFilePreview(URL.createObjectURL(file))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate required fields
    if (!formData.title || !formData.description || !formData.source) {
      toast.error("Please fill in all required fields")
      return
    }

    try {
      // Send only the necessary data to the API
      const mediaData = {
        title: formData.title,
        description: formData.description,
        source: formData.source,
        file_link: formData.file_link || null, // Optional
        media_file: formData.media_file, // Optional binary file
      }

      await addMediaMutation.mutateAsync(mediaData)
      setFormData(initialState)
      setFilePreview(null)
      onOpenChange(false)
      toast.success("Media added successfully")
    } catch (error) {
      toast.error(error?.message || "Failed to add media")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add Media</DialogTitle>
          <DialogDescription>
            Add media content related to the doctor.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="Media title"
                required
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Media description"
                required
              />
            </div>

            <div>
              <Label>Source</Label>
              <Input
                value={formData.source}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, source: e.target.value }))
                }
                placeholder="Media source"
                required
              />
            </div>

            <div>
              <Label>File Link (Optional)</Label>
              <Input
                value={formData.file_link}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    file_link: e.target.value,
                  }))
                }
                placeholder="URL"
                type="url"
              />
            </div>

            <div>
              <Label>Media File</Label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-4">
                  <Input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    id="media-file"
                    accept="image/*,video/*"
                  />
                  <Label
                    htmlFor="media-file"
                    className="flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer hover:bg-gray-50"
                  >
                    <FileUp className="h-4 w-4" />
                    Choose File
                  </Label>
                </div>
                {formData.media_file && (
                  <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-md flex items-center justify-between">
                    <span className="truncate">{formData.media_file.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, media_file: null }))
                        setFilePreview(null)
                      }}
                    >
                      ×
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setFormData(initialState)
                setFilePreview(null)
                onOpenChange(false)
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={addMediaMutation.isPending}>
              {addMediaMutation.isPending ? "Adding..." : "Add Media"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
